import type { RadioState, RadioStatus } from './radio';
import { RADIO_STATIONS } from './radioStations';

/**
 * The radio's state in words, the same under the lamps on the page and on the headset's radio
 * plate. A lit lamp alone said nothing of a stream still tuning in, lost, held back by the
 * browser or paused (client-audio-engine:quality#3). Short enough for one line under the lamps.
 */
export function radioStatusText(
  radio: Pick<RadioState, 'status' | 'selected'>,
  outOfOrder = false,
): string {
  switch (radio.status) {
    case 'off':
      return 'Radio off';
    case 'idle':
      return 'Press the lit lamp to play';
    case 'connecting':
      return 'Tuning in…';
    case 'playing': {
      const station = RADIO_STATIONS.find((candidate) => candidate.id === radio.selected);
      return station ? `Playing ${station.name}` : 'Playing';
    }
    case 'blocked':
      // The browser wants a press first; pressing the lamp is one, on the page and in VR.
      return 'Press the lamp again to play';
    case 'lost':
      return outOfOrder ? 'Off the air · try another station' : 'Signal lost · trying again';
    case 'asleep':
      return 'Paused after 4 hours · press the lamp';
  }
}

/** A lamp that is lit but not playing, and why: still tuning in, or in trouble. */
export function radioLampState(status: RadioStatus): 'tuning' | 'trouble' | null {
  if (status === 'connecting') return 'tuning';
  if (status === 'lost' || status === 'blocked' || status === 'asleep') return 'trouble';
  return null;
}
